import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { PROXY_BASE } from '../config/apiConfig';
import { getAuthHeaders } from '../services/authService';
import AssetDetail from './AssetDetail';
import './EarningsCalendarView.css';

// ── Logo helpers ──────────────────────────────────────────────────────────────

function getLogoUrl(ticker) {
  return `https://assets.parqet.com/logos/symbol/${ticker}?format=svg`;
}

function TickerAvatar({ ticker, name }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    const initials = (name || ticker).replace(/[^A-Za-zÄÖÜäöü0-9]/g, '').slice(0, 2).toUpperCase();
    return <div className="ec-avatar ec-avatar--fallback">{initials || ticker.slice(0, 2)}</div>;
  }
  return (
    <div className="ec-avatar">
      <img src={getLogoUrl(ticker)} alt="" onError={() => setFailed(true)} />
    </div>
  );
}

// ── Date helpers ──────────────────────────────────────────────────────────────

const MONTHS = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
const WEEKDAYS = ['Sonntag','Montag','Dienstag','Mittwoch','Donnerstag','Freitag','Samstag'];

function startOfDay(ts) {
  const d = new Date(ts);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function daysFromToday(ts) {
  return Math.round((startOfDay(ts) - startOfDay(Date.now())) / 86400000);
}

// ── Erwartungs-Sentiment (1:1 wie AssetDetail) ───────────────────────────────

function sentimentFor(ev) {
  const eg = ev.epsGrowth;
  const rg = ev.revenueGrowth;
  if (eg == null && rg == null) return null;
  const eps = eg ?? rg;
  const rev = rg ?? eg;
  let score = eps * 0.6 + rev * 0.4;
  const up = ev.revisionsUp || 0;
  const down = ev.revisionsDown || 0;
  const total = up + down;
  if (total >= 5) {
    const bias = (up - down) / total;
    if (bias > 0.5) score += 0.08;
    else if (bias < -0.5) score -= 0.08;
  }
  if (score >= 0.40)  return { label: 'Sehr stark',    tone: 'pos-strong', fill: 5, score };
  if (score >= 0.15)  return { label: 'Stark positiv', tone: 'pos-strong', fill: 4, score };
  if (score >= 0.05)  return { label: 'Positiv',       tone: 'pos',        fill: 4, score };
  if (score >= -0.05) return { label: 'Stabil',        tone: 'neutral',    fill: 3, score };
  if (score >= -0.15) return { label: 'Verhalten',     tone: 'warn',       fill: 2, score };
  if (score >= -0.30) return { label: 'Schwach',       tone: 'neg',        fill: 2, score };
                       return { label: 'Sehr schwach', tone: 'neg-strong', fill: 1, score };
}

function fmtPct(v) {
  if (v == null) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${(v * 100).toFixed(1)}%`;
}

function pctClass(v) {
  if (v == null) return '';
  if (v > 0) return 'pos';
  if (v < 0) return 'neg';
  return '';
}

// ── Sentiment-Gauge (1:1 wie AssetDetail.detail-event-gauge) ─────────────────

function SentimentGauge({ sentiment }) {
  if (!sentiment) {
    return <span className="ec-sent-empty">Keine Analysten-Schätzungen</span>;
  }
  return (
    <div className="ec-sent">
      <div className={`ec-gauge tone-${sentiment.tone}`}>
        {[1,2,3,4,5].map(n => (
          <span key={n} className={`ec-gauge-seg ${n <= sentiment.fill ? 'is-on' : ''}`} />
        ))}
      </div>
      <span className="ec-sent-text">
        <span className="ec-sent-label">Erwartungen</span>
        <span className={`ec-sent-value tone-${sentiment.tone}`}>{sentiment.label}</span>
      </span>
    </div>
  );
}

// ── Konfiguration ─────────────────────────────────────────────────────────────

const FUTURE_DAYS = 30;
const PAST_DAYS = 7;

function fmtNum(v, digits = 2) {
  if (v == null) return '—';
  return v.toFixed(digits);
}

// ── Event-Row (Listen-Optik, keine Card) ─────────────────────────────────────

function EventRow({ ev, onClick }) {
  const s = sentimentFor(ev);
  const hasRevisions = (ev.revisionsUp || ev.revisionsDown);

  return (
    <button type="button" className={`ec-row${s ? ` ec-row-tone-${s.tone}` : ''}`} onClick={onClick}>
      <TickerAvatar ticker={ev.ticker} name={ev.name} />

      <div className="ec-row-body">
        <div className="ec-row-head">
          <span className="ec-row-name">{ev.name}</span>
          <span className="ec-row-ticker">{ev.ticker}</span>
        </div>

        <div className="ec-meta">
          <span className="ec-meta-cell">
            <span className="ec-meta-key">EPS</span>
            <span className={`ec-meta-val ${pctClass(ev.epsGrowth)}`}>{fmtPct(ev.epsGrowth)}</span>
          </span>
          <span className="ec-meta-cell">
            <span className="ec-meta-key">Umsatz</span>
            <span className={`ec-meta-val ${pctClass(ev.revenueGrowth)}`}>{fmtPct(ev.revenueGrowth)}</span>
          </span>
          {hasRevisions ? (
            <span className="ec-meta-cell">
              <span className="ec-meta-key">Revisions</span>
              <span className="ec-meta-val">
                <span className="ec-rev-up">↑{ev.revisionsUp || 0}</span>
                <span className="ec-rev-sep">·</span>
                <span className="ec-rev-down">↓{ev.revisionsDown || 0}</span>
              </span>
            </span>
          ) : null}
        </div>

        <SentimentGauge sentiment={s} />
      </div>
    </button>
  );
}

// ── Released-Card (für Past-Earnings im Top-Swipe) ───────────────────────────

function ReleasedCard({ ev, onClick }) {
  const beat = (typeof ev.surprisePercent === 'number') ? ev.surprisePercent : null;
  const beatPct = beat != null ? Math.round(beat * 1000) / 10 : null;
  let beatTone = 'neutral';
  if (beatPct != null) {
    if (beatPct >= 5) beatTone = 'pos-strong';
    else if (beatPct >= 1) beatTone = 'pos';
    else if (beatPct <= -5) beatTone = 'neg-strong';
    else if (beatPct <= -1) beatTone = 'neg';
  }
  const beatLabel = beatPct == null ? null : (beatPct > 0 ? 'Beat' : beatPct < 0 ? 'Miss' : 'Inline');
  const beatStr = beatPct == null ? null : `${beatPct > 0 ? '+' : ''}${beatPct}%`;
  return (
    <button type="button" className={`ec-released-card tone-${beatTone}`} onClick={onClick}>
      <div className="ec-released-top">
        <TickerAvatar ticker={ev.ticker} name={ev.name} />
        <div className="ec-released-id">
          <span className="ec-released-name">{ev.name}</span>
          <span className="ec-released-ticker">{ev.ticker}</span>
        </div>
        {beatLabel && (
          <span className={`ec-released-badge tone-${beatTone}`}>{beatLabel}</span>
        )}
      </div>
      <div className="ec-released-mid">
        <div className="ec-released-eps-cell">
          <span className="ec-released-eps-key">EPS Ist</span>
          <span className="ec-released-eps-val">{fmtNum(ev.epsActual)}</span>
        </div>
        <div className="ec-released-eps-divider" />
        <div className="ec-released-eps-cell">
          <span className="ec-released-eps-key">Erwartet</span>
          <span className="ec-released-eps-val ec-released-eps-est">{fmtNum(ev.epsEstimate)}</span>
        </div>
      </div>
      {beatStr && (
        <div className={`ec-released-beat-bar tone-${beatTone}`}>
          <span className="ec-released-beat-num">{beatStr}</span>
          <span className="ec-released-beat-vs">vs. Konsens</span>
        </div>
      )}
    </button>
  );
}

// ── Day-Block: bei >1 Event → horizontal swipe-fähig (scroll-snap) ───────────

function DayBlock({ ts, events, onAssetOpen }) {
  const date = new Date(ts);
  const d = daysFromToday(ts);
  const wd = WEEKDAYS[date.getDay()];
  const month = MONTHS[date.getMonth()];
  const day = date.getDate();

  let pill = null;
  if (d === 0)      pill = <span className="ec-day-pill is-today">heute</span>;
  else if (d === 1) pill = <span className="ec-day-pill is-tomorrow">morgen</span>;

  return (
    <section className="ec-day">
      <header className="ec-day-head">
        <div className="ec-day-stamp">
          <span className="ec-day-num">{String(day).padStart(2,'0')}</span>
          <div className="ec-day-meta">
            <span className="ec-day-month">{month}</span>
            <span className="ec-day-weekday">{wd}</span>
          </div>
        </div>
        <div className="ec-day-aside">
          {pill}
          <span className="ec-day-count">№ {String(events.length).padStart(2,'0')}</span>
        </div>
      </header>

      <div className="ec-day-list">
        {events.map((ev) => (
          <EventRow
            key={ev.ticker + ev.ts}
            ev={ev}
            onClick={() => onAssetOpen(ev)}
          />
        ))}
      </div>
    </section>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────

const CACHE_KEY = 'bullivo_termine_cache_v1';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1h

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { ts, events } = JSON.parse(raw);
    if (!Array.isArray(events) || typeof ts !== 'number') return null;
    return { ts, events, fresh: (Date.now() - ts) < CACHE_TTL_MS };
  } catch {
    return null;
  }
}

function writeCache(events) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), events }));
  } catch { /* quota exceeded — ignore */ }
}

function EarningsCalendarView({ assets = [], onRegisterDetailCloser }) {
  // Stale-While-Revalidate: beim Mount sofort aus Cache rendern (wenn vorhanden)
  const cached = useMemo(() => readCache(), []);
  const [events, setEvents] = useState(cached?.events ?? null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(!cached); // kein Loading wenn Cache da
  const [selectedAsset, setSelectedAsset] = useState(null);

  useEffect(() => {
    onRegisterDetailCloser?.(selectedAsset ? () => setSelectedAsset(null) : null);
  }, [selectedAsset, onRegisterDetailCloser]);

  // Body-Klasse setzen, damit AppHeader/App-content auf diesem Tab transparent werden
  useEffect(() => {
    document.body.classList.add('ec-tab-active');
    return () => document.body.classList.remove('ec-tab-active');
  }, []);

  const loadEvents = useCallback(async ({ silent } = {}) => {
    if (!silent) { setLoading(true); setError(false); }
    try {
      const r = await fetch(`${PROXY_BASE}/api/earnings-calendar`, {
        headers: { Accept: 'application/json', ...(await getAuthHeaders()) },
      });
      if (!r.ok) throw new Error(`http ${r.status}`);
      const data = await r.json();
      const list = Array.isArray(data?.events) ? data.events : [];
      setEvents(list);
      if (list.length > 0) writeCache(list);
    } catch {
      // Bei Fehler: wenn schon Cache-Daten gerendert sind, kein Error-State (still keep cache)
      if (!silent) setError(true);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Beim Mount: laden — silent wenn Cache fresh (<1h), sonst sichtbar
  useEffect(() => { loadEvents({ silent: cached?.fresh === true }); }, [loadEvents, cached?.fresh]);

  const openAsset = useCallback((ev) => {
    const enriched = assets.find(a => a.ticker === ev.ticker);
    setSelectedAsset(enriched || { ticker: ev.ticker, name: ev.name });
  }, [assets]);

  // Past-Events: nur "earningsPast" der letzten PAST_DAYS Tage, sortiert marketCap DESC
  const released = useMemo(() => {
    if (!events) return [];
    const cutoff = Date.now() - PAST_DAYS * 86400000;
    return events
      .filter(e => e.type === 'earningsPast' && e.ts >= cutoff && e.ts <= Date.now())
      .sort((a, b) => (b.marketCap || 0) - (a.marketCap || 0));
  }, [events]);

  // Future-Events: 60 Tage Horizont, gruppiert nach Tag, pro Tag marketCap DESC
  const grouped = useMemo(() => {
    if (!events) return [];
    const horizon = Date.now() + FUTURE_DAYS * 86400000;
    const today = startOfDay(Date.now());
    const filtered = events.filter(e => e.type === 'earnings' && e.ts >= today && e.ts <= horizon);
    const byDay = new Map();
    for (const e of filtered) {
      const key = startOfDay(e.ts);
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push(e);
    }
    return Array.from(byDay.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([ts, items]) => ({
        ts,
        events: items.sort((a, b) => (b.marketCap || 0) - (a.marketCap || 0)),
      }));
  }, [events]);

  const totalCount = grouped.reduce((s, g) => s + g.events.length, 0);

  return (
    <div className="ec-view">
      <div className="ec-hero">
        <div className="ec-hero-blur" />
        <div className="ec-hero-content">
          <div className="ec-hero-text">
            <span className="ec-hero-eyebrow">Earnings · Kalender</span>
            <h1 className="ec-hero-title">Termine</h1>
            <p className="ec-hero-desc">
              Quartalszahlen der kommenden 30 Tage mit Analysten-Erwartungen.
            </p>
          </div>
          <div className="ec-hero-stat">
            <span className="ec-hero-stat-label">Im Zeitraum</span>
            <span className="ec-hero-stat-value">{String(totalCount).padStart(2,'0')}</span>
          </div>
        </div>
      </div>

      {released.length > 0 && (
        <section className="ec-released">
          <header className="ec-released-head-bar">
            <span className="ec-released-eyebrow">Soeben veröffentlicht</span>
            <span className="ec-released-count">{released.length} {released.length === 1 ? 'Aktie' : 'Aktien'}</span>
          </header>
          <div className="ec-released-carousel">
            {released.map((ev) => (
              <ReleasedCard key={ev.ticker + ev.ts} ev={ev} onClick={() => openAsset(ev)} />
            ))}
          </div>
        </section>
      )}

      {loading && (
        <div className="ec-skeletons">
          {[1,2,3].map(i => <div key={i} className="ec-skeleton shimmer" />)}
        </div>
      )}

      {!loading && error && (
        <div className="ec-error">
          <p>⚠️ Termine konnten nicht geladen werden</p>
          <button onClick={loadEvents}>Neu laden</button>
        </div>
      )}

      {!loading && !error && totalCount === 0 && (
        <p className="ec-empty">Keine Termine in diesem Zeitraum.</p>
      )}

      {!loading && !error && totalCount > 0 && (
        <div className="ec-stream">
          {grouped.map(g => (
            <DayBlock key={g.ts} ts={g.ts} events={g.events} onAssetOpen={openAsset} />
          ))}
        </div>
      )}

      {selectedAsset && (
        <AssetDetail asset={selectedAsset} onClose={() => setSelectedAsset(null)} />
      )}

      <p className="ec-disclaimer">
        Quelle Yahoo Finance · Konsens-Schätzungen können bis kurz vor Veröffentlichung schwanken
      </p>
    </div>
  );
}

export default EarningsCalendarView;
